'use client'

import AppIcon from '@/components/content/app/AppIcon'
import Badge from '@/components/ui/Badge'
import { CourseCallToActionItem } from '@/components/content/courses/CourseCallToAction'
import { AuthorProfile } from '@/components/content/common/Authors'

import { generateRating } from '@/components/ui/Testimonials'
import { getBrandColors } from '@/lib/utils'

import type { Course, App, User } from '@/payload-types'

type CourseLevelType = 'Beginner' | 'Intermediate' | 'Advanced'
type InstructorProps = Pick<User, 'id' | 'name' | 'image' | 'authorDescription' | 'headshot'>

type CoursesHeroProps = {
  course: Pick<Course, 'id' | 'name' | 'trustedBy' | 'duration'>
  app: App
  instructor: InstructorProps
}

type CourseInfoProps = {
  instructor: InstructorProps
  rating: number
  level: CourseLevelType
  trustedBy?: string
  duration?: string
}

export default function CoursesHeroContent({ course, app, instructor }: CoursesHeroProps) {
  const rating = app.name === 'Motion' ? 4.9 : 4.6

  return (
    <>
      <div className="flex flex-col gap-8">
        <CourseApp app={app} />
        <div className="flex flex-col gap-4">
          <h1 className="text-center sm:text-left">{course.name}</h1>
          <p className="text-center text-white/60 sm:text-left">{app.description}</p>
        </div>
        <CourseInfo
          instructor={instructor}
          rating={rating}
          level="Beginner"
          trustedBy={course.trustedBy || undefined}
          duration={course.duration || undefined}
        />
      </div>
    </>
  )
}

export function CourseApp({ app }: { app: App }) {
  const { backgroundColor } = getBrandColors(app)

  return (
    <div className="flex items-center gap-5 px-5 sm:gap-7 sm:px-0">
      <AppIcon
        icon={app.icon}
        name={app.name}
        slug={app.slug}
        background={backgroundColor}
        size={80}
      />

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2.5">
          <span className="text-20 font-bold">{app.name}</span>
          <Badge style="primary" icon="best" nested>
            Best
          </Badge>
        </div>

        <p className="line-clamp-2 text-13 text-zinc-400">{app.description}</p>
      </div>
    </div>
  )
}

export function CourseInfo({ instructor, rating, level, trustedBy, duration }: CourseInfoProps) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-8">
        <AuthorProfile author={instructor} theme="dark" nested={true} />

        <div className="flex items-center gap-3.5">
          {generateRating(rating)}
          <span className="text-13 font-medium text-white">{rating}</span>
        </div>

        <CourseLevel level={level} />
      </div>

      {(trustedBy || duration) && (
        <div className="flex items-center justify-center gap-8 sm:justify-start">
          {trustedBy && (
            <CourseCallToActionItem
              icon="users"
              label="Trusted by"
              value={trustedBy}
              theme="dark"
            />
          )}
          {duration && (
            <CourseCallToActionItem icon="circle-half-dotted-clock" value={duration} theme="dark" />
          )}
        </div>
      )}
    </div>
  )
}

export function CourseLevel({ level }: { level: CourseLevelType }) {
  const levels = {
    Beginner: 1,
    Intermediate: 2,
    Advanced: 3,
  }

  const bar1 = { fill: '#FF3465', stroke: '#FF3465', opacity: 1 }
  const bar2 = {
    fill: levels[level] > 1 ? '#FF3465' : 'none',
    stroke: levels[level] > 1 ? '#FF3465' : 'white',
    opacity: levels[level] > 1 ? 1 : 0.3,
  }
  const bar3 = {
    fill: levels[level] > 2 ? '#FF3465' : 'none',
    stroke: levels[level] > 2 ? '#FF3465' : 'white',
    opacity: levels[level] > 2 ? 1 : 0.3,
  }

  return (
    <div className="flex items-center gap-3">
      <svg
        width="16"
        height="14"
        viewBox="0 0 16 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity={bar1.opacity}
          d="M2.75 9.75H2.25C1.69772 9.75 1.25 10.1977 1.25 10.75V12.25C1.25 12.8023 1.69772 13.25 2.25 13.25H2.75C3.30228 13.25 3.75 12.8023 3.75 12.25V10.75C3.75 10.1977 3.30228 9.75 2.75 9.75Z"
          fill={bar1.fill}
          stroke={bar1.stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          opacity={bar2.opacity}
          d="M8.25 5.75H7.75C7.19772 5.75 6.75 6.19772 6.75 6.75V12.25C6.75 12.8023 7.19772 13.25 7.75 13.25H8.25C8.80228 13.25 9.25 12.8023 9.25 12.25V6.75C9.25 6.19772 8.80228 5.75 8.25 5.75Z"
          fill={bar2.fill}
          stroke={bar2.stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          opacity={bar3.opacity}
          d="M13.75 0.75H13.25C12.6977 0.75 12.25 1.19772 12.25 1.75V12.25C12.25 12.8023 12.6977 13.25 13.25 13.25H13.75C14.3023 13.25 14.75 12.8023 14.75 12.25V1.75C14.75 1.19772 14.3023 0.75 13.75 0.75Z"
          fill={bar3.fill}
          stroke={bar3.stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <span className="text-13 font-medium text-white">{level}</span>
    </div>
  )
}
